import { StatusDocumentUpload } from '@/components/define-document-upload/define-document-upload.config';
import { DocumentTypeEnum, PageTypeEnum } from '@/constant';
import { dayjs, randomId, SUPPORT_FORMAT_DATE_LIST } from '@/utils';
import { isMobile } from '@/utils/mobile.util';
import { PayloadAction } from '@reduxjs/toolkit';
import { put, select } from 'redux-saga/effects';

import * as API from 'services';

import { included } from 'ramda-adjunct';

import {
	createMapping,
	DOCUMENT_DEFAULT,
	formatDefineDocumentUploadList,
	TARGET_PAGES,
} from './define-document-upload.helper';
import {
	DefineDocumentUploadActions,
	DefineDocumentUploadSelectors,
} from './define-document-upload.redux';
import {
	TPayloadGetDefineDocumentUploadListRequest,
	TPayloadUpdateDocumentRequest,
} from './define-document-upload.type';

type TBaseItem = {
	refTableName: PageTypeEnum;
	refTableId: number;
	documentType: DocumentTypeEnum | string;
	registDt?: string;
	statusDocumentUpload?: StatusDocumentUpload;
	fileList?: API.TDocumentUploadDefineItem;
};

const checkProgramDate = (
	item: TBaseItem,
	programId?: string | number,
	programCurrent?: { startDate?: string; endDate?: string }
): boolean => {
	if (!programId) {
		return true;
	}

	if (DOCUMENT_DEFAULT.includes(item.documentType as DocumentTypeEnum)) {
		return true;
	}

	if (item?.statusDocumentUpload === StatusDocumentUpload.ADD) {
		return true;
	}

	if (
		!item.registDt ||
		!programCurrent?.startDate ||
		!programCurrent?.endDate
	) {
		return false;
	}

	return dayjs(item.registDt, SUPPORT_FORMAT_DATE_LIST).isBetween(
		dayjs(programCurrent.startDate, SUPPORT_FORMAT_DATE_LIST),
		dayjs(programCurrent.endDate, SUPPORT_FORMAT_DATE_LIST),
		null,
		'[]'
	);
};

type TItemWithFileList = TBaseItem & {
	fileList?: any;
	mappingList?: any[];
	fileName?: string;
	typeName?: string;
	statusDocumentUpload?: string;
};

const filterWithProgramDate = <T extends TItemWithFileList>(
	items: T[],
	programId?: string | number,
	programCurrent?: { startDate?: string; endDate?: string }
): T[] => {
	if (!programId) {
		return items;
	}

	// Group items by documentType
	const groupedByDocumentType = items.reduce(
		(acc, item) => {
			const docType = String(item.documentType);
			if (!acc[docType]) {
				acc[docType] = [];
			}
			acc[docType].push(item);
			return acc;
		},
		{} as Record<string, T[]>
	);

	const filteredResult: T[] = [];

	// Process each documentType group
	Object.keys(groupedByDocumentType).forEach((docType) => {
		const groupItems = groupedByDocumentType[docType];

		// Filter items with checkProgramDate and update status if needed
		const filteredGroup = groupItems.filter((item) =>
			checkProgramDate(item, programId, programCurrent)
		);

		// If all items are filtered out, add a default item
		// This ensures each documentType always has at least one item
		// (e.g., when all items have status DELETE and don't pass checkProgramDate)
		if (filteredGroup.length === 0 && groupItems.length > 0) {
			// Find the first item without fileList (default item) or use the first item
			const defaultItem =
				groupItems.find(
					(item) =>
						!item.fileList ||
						Object.keys(item.fileList as Record<string, unknown>).length === 0
				) || groupItems[0];

			// Create a new default item with status ADD to ensure it passes checkProgramDate
			const newDefaultItem = {
				...defaultItem,
				id: randomId(),
				fileList: {} as API.TDocumentUploadDefineItem,
				fileName: '',
				typeName: '-',
				statusDocumentUpload: StatusDocumentUpload.ADD,
				hidden: false,
				modified: false,
				registDt: '',
				mappingList: [createMapping('' as unknown as PageTypeEnum, null)],
			} as T;

			filteredResult.push(newDefaultItem);
		} else {
			filteredResult.push(...filteredGroup);
		}
	});

	return filteredResult;
};

export function* getDefineDocumentUploadListByPage(
	action: PayloadAction<TPayloadGetDefineDocumentUploadListRequest>
) {
	const { callback, programId, programCurrent, ...restParams } = action.payload;

	try {
		// Commented out because it is not used, below is handling filter at local instead of api
		const currentState: ReturnType<
			typeof DefineDocumentUploadSelectors.selectDefineDocumentUploadListFull
		> = yield select(
			DefineDocumentUploadSelectors.selectDefineDocumentUploadListFull
		);

		if (currentState.length > 0 && !isMobile) {
			// First filter by other conditions
			const preFilteredList = currentState.filter((item) => {
				const documentTypeOk = restParams.documentType?.length
					? included(restParams.documentType, item.documentType)
					: true;

				const applicantOk = restParams?.applicantId
					? item.mappingList?.some(
							(ml) =>
								ml?.refTableName === PageTypeEnum.APPLICANT &&
								ml?.refTableId === Number(restParams?.applicantId)
						)
					: true;

				const franchiseOk = restParams?.franchiseId
					? item.mappingList?.some(
							(ml) =>
								ml?.refTableName === PageTypeEnum.FRANCHISE &&
								ml?.refTableId === Number(restParams?.franchiseId)
						)
					: true;

				return documentTypeOk && applicantOk && franchiseOk;
			});

			// Then filter by programDate and ensure each documentType has at least one item
			const filteredList = filterWithProgramDate(
				preFilteredList,
				programId,
				programCurrent
			);

			yield put(
				DefineDocumentUploadActions.getDefineDocumentUploadListSuccess({
					defineDocumentUploadList: filteredList,
				})
			);
			callback?.();
			return;
		}

		const res: API.ApiResponseSuccess<API.TGetDocumentUploadListResponse> =
			yield API.getCommonDocument(restParams);

		let relationDocumentRes =
			{} as API.ApiResponseSuccess<API.TRelationDocumentInfo>;

		if (TARGET_PAGES.includes(restParams.refTableName)) {
			relationDocumentRes = yield API.getRelationDocumentInfo({
				id: restParams.refTableId,
				tableEnum: restParams.refTableName,
			});
		}

		const defineDocumentUploadList = formatDefineDocumentUploadList(
			res?.data,
			relationDocumentRes?.data
		);

		// Filter by programDate and ensure each documentType has at least one item
		const filteredList = filterWithProgramDate(
			defineDocumentUploadList,
			programId,
			programCurrent
		);

		yield put(
			DefineDocumentUploadActions.getDefineDocumentUploadListSuccess({
				defineDocumentUploadList: filteredList,
			})
		);
		yield put(
			DefineDocumentUploadActions.getDefineDocumentUploadListFull({
				defineDocumentUploadList,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(DefineDocumentUploadActions.getDefineDocumentUploadListFailure());
	}
}

export function* updateDocumentUpload(
	action: PayloadAction<TPayloadUpdateDocumentRequest>
) {
	const { callback, ...restParams } = action.payload;

	try {
		yield API.updateDocument(restParams);

		yield put(DefineDocumentUploadActions.updateDocumentSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(DefineDocumentUploadActions.updateDocumentFailure());
	}
}
